import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  GraduationCap, Smartphone, Download, CheckCircle, Star, 
  BookOpen, MessageCircle, Bell, Shield, ArrowLeft 
} from "lucide-react";

// App Download URL - Update this when new builds are available
const APP_DOWNLOAD_URL = "https://expo.dev/accounts/saumya123/projects/descriptive-test/builds";
const DIRECT_APK_URL = "https://expo.dev/artifacts/eas/4jM9zyTyA9Bnhqvc4MEdZL.apk";

export default function AppDownload() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Helmet>
        <title>Download Descriptive Test App - EMRS KVS NVS TGT PGT Exam Preparation</title>
        <meta name="description" content="Download the Descriptive Test Android app for EMRS, KVS, NVS TGT PGT PRT Tier-2 exam preparation. Practice mock tests, descriptive questions, MCQs on your mobile. Offline study notes, AI-powered answer evaluation." />
        <meta name="keywords" content="Descriptive Test app download, EMRS exam app, KVS preparation app, NVS exam app, TGT PGT app, teaching exam app, EMRS mock test app, KVS MCQ app" />
        <meta property="og:title" content="Download Descriptive Test App - EMRS KVS NVS Preparation" />
        <meta property="og:description" content="Get the best exam preparation experience for EMRS, KVS, NVS on your Android device." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://descriptivetest.com/app" />
      </Helmet>
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
            <GraduationCap className="w-8 h-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">Descriptive Test</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Download Our App
          </h1>
          <p className="text-lg text-white/70 max-w-xl mx-auto">
            Get the best exam preparation experience on your Android device
          </p>
        </div>

        {/* Main Download Card */}
        <Card className="bg-white/10 backdrop-blur-md border-white/20 mb-8">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* App Icon */}
              <div className="w-32 h-32 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl">
                <GraduationCap className="w-16 h-16 text-white" />
              </div>

              {/* App Info */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold text-white mb-2">Descriptive Test</h2>
                <p className="text-white/70 mb-4">EMRS, KVS, NVS Exam Preparation</p>
                
                <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-6">
                  <Badge className="bg-purple-500/30 text-purple-200 border-purple-400/30">
                    <Star className="w-3 h-3 mr-1 fill-current" /> 4.8 Rating
                  </Badge>
                  <Badge className="bg-green-500/30 text-green-200 border-green-400/30">
                    v1.1.0
                  </Badge>
                  <Badge className="bg-blue-500/30 text-blue-200 border-blue-400/30">
                    Android
                  </Badge>
                </div>

                {/* Download Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <a
                    href={DIRECT_APK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    <Download className="w-5 h-5" />
                    Download APK
                  </a>
                  <a
                    href={APP_DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-semibold border border-white/20 transition-all"
                  >
                    <Smartphone className="w-5 h-5" />
                    View All Builds
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features Grid */}
        <h3 className="text-xl font-bold text-white mb-6 text-center">App Features</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {[
            { icon: BookOpen, title: "Study Notes", desc: "CBSE & NCERT based notes" },
            { icon: CheckCircle, title: "MCQ Practice", desc: "Topic-wise MCQ tests" },
            { icon: GraduationCap, title: "Descriptive Q's", desc: "Subjective questions" },
            { icon: MessageCircle, title: "Peer Chat", desc: "Connect with students" },
            { icon: Bell, title: "Notifications", desc: "Stay updated" },
            { icon: Shield, title: "NCERT Exemplar", desc: "All class questions" },
          ].map((feature, idx) => (
            <Card key={idx} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
              <CardContent className="p-4 flex items-start gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <feature.icon className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">{feature.title}</h4>
                  <p className="text-sm text-white/60">{feature.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Installation Instructions */}
        <Card className="bg-white/5 border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="text-white">How to Install</CardTitle>
            <CardDescription className="text-white/60">
              Follow these simple steps to install the app
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              "Click 'Download APK' button above",
              "Open the downloaded file on your Android device",
              "If prompted, enable 'Install from unknown sources' in settings",
              "Complete the installation and open the app",
              "Register or login with your mobile number"
            ].map((step, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <p className="text-white/80">{step}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Contact Section */}
        <div className="text-center text-white/60 text-sm">
          <p>Need help? Contact us at</p>
          <p className="text-purple-400 font-medium">
            Phone: 8076623513 | Email: Lokeshbhardwaj.sir@gmail.com
          </p>
        </div>

        {/* Footer Links */}
        <div className="text-center mt-8 space-x-4 text-sm">
          <Link to="/privacy-policy" className="text-white/60 hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms-and-conditions" className="text-white/60 hover:text-white transition-colors">
            Terms & Conditions
          </Link>
          <Link to="/refund-policy" className="text-white/60 hover:text-white transition-colors">
            Refund Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
